/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on event-handler/async setState */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarDays, Newspaper, Image, DollarSign, Tag, BookOpen, LogOut, Users, QrCode, Shield, Crown, Mail, FolderDown, RefreshCw, ScrollText, HardDrive, ArrowRightLeft, DownloadCloud, Upload } from "lucide-react";
import axios from "axios";

// Import refactored admin components
import {
  MembersTab,
  AdminsTab,
  ClubsTab,
  SponsorsTab,
  EmailTab,
  DownloadsTab,
  ScrapeTab,
  LogsTab,
  ContentDialog,
  AdminContentTabs,
} from "@/components/admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLUBS = [
  { id: "ninersbillard", name: "Ninersbillard" },
  { id: "dsv-niners", name: "DSV Niners Wels" },
  { id: "sdv-niners", name: "SDV Niners Wels" },
  { id: "bsv-niners", name: "BSV Niners Wels" },
  { id: "perchten", name: "D´Wösa Höllen Viecha" },
  { id: "soundtheatre", name: "The Soundtheatre" },
  { id: "vip", name: "Niners-VIP Club" },
  { id: "sportuebertragungen", name: "Sportübertragungen" },
  { id: "shop", name: "Niners-Shop" },
];

function useApi() {
  const token = localStorage.getItem("nb_token");
  const headers = { Authorization: `Bearer ${token}` };
  return {
    get: (url) => axios.get(`${API}${url}`, { headers }),
    post: (url, data, config) => axios.post(`${API}${url}`, data, { ...config, headers: { ...headers, ...config?.headers } }),
    put: (url, data, config) => axios.put(`${API}${url}`, data, { ...config, headers: { ...headers, ...config?.headers } }),
    del: (url) => axios.delete(`${API}${url}`, { headers }),
    delete: (url) => axios.delete(`${API}${url}`, { headers }),
    upload: (url, data) => axios.post(`${API}${url}`, data, { headers }),
  };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const api = useApi();
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [pricelist, setPricelist] = useState([]);
  const [offers, setOffers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [formData, setFormData] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const restoreInputRef = useRef(null);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMembers, setSyncMembers] = useState(false);

  const isCentral = user?.role === "central_admin";
  const managedClubs = isCentral ? CLUBS : CLUBS.filter(c => c.id === user?.club_id || c.id === "sportuebertragungen");

  const loadData = useCallback(() => {
    if (!user) return;
    api.get("/events").then(r => setEvents(r.data)).catch(() => {});
    api.get("/news").then(r => setNews(r.data)).catch(() => {});
    api.get("/gallery").then(r => setGallery(r.data)).catch(() => {});
    if (isCentral) {
      api.get("/pricelist").then(r => setPricelist(r.data)).catch(() => {});
      api.get("/special-offers?active_only=false").then(r => setOffers(r.data)).catch(() => {});
    }
    api.get("/reservations").then(r => setReservations(r.data)).catch(() => {});
  }, [user, isCentral, api]);

  useEffect(() => {
    const token = localStorage.getItem("nb_token");
    if (!token) { navigate("/admin-login"); return; }
    const stored = localStorage.getItem("nb_user");
    if (stored) setUser(JSON.parse(stored));
    else {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { setUser(r.data); localStorage.setItem("nb_user", JSON.stringify(r.data)); })
        .catch(() => navigate("/admin-login"));
    }
  }, [navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const logout = () => {
    localStorage.removeItem("nb_token");
    localStorage.removeItem("nb_user");
    navigate("/admin-login");
  };

  const downloadBackup = async () => {
    setBackupLoading(true);
    try {
      const token = localStorage.getItem("nb_token");
      const res = await axios.get(`${API}/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || "backup.zip";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Backup heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup fehlgeschlagen");
    } finally {
      setBackupLoading(false);
    }
  };

  const migrateAssets = async () => {
    const token = localStorage.getItem("nb_token");
    const headers = { Authorization: `Bearer ${token}` };
    setMigrateLoading(true);
    try {
      // First dry run
      const dry = await axios.post(`${API}/admin/migrate-assets?dry_run=true`, null, { headers });
      if (dry.data.status !== "started") throw new Error("Start fehlgeschlagen");
      toast.info("Prüfe externe Assets...");
      // Poll for result
      let result = null;
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const status = await axios.get(`${API}/admin/migrate-assets/status`, { headers });
        if (!status.data.running && status.data.result) { result = status.data.result; break; }
      }
      if (!result) { toast.error("Timeout bei Prüfung"); return; }
      if (result.found === 0) { toast.success("Keine externen Assets gefunden — alles OK!"); return; }
      // Ask to migrate — list the concrete assets found
      const list = (result.details || []).map((d, i) =>
        `${i + 1}. [${d.type}] ${d.collection}${d.title && d.title !== "?" ? ` – "${d.title}"` : ""} (${d.field})\n   ${d.old_url}`
      ).join("\n\n");
      if (!window.confirm(`${result.found} Asset(s) gefunden:\n\n${list}\n\nJetzt migrieren?`)) return;
      // Real migration
      await axios.post(`${API}/admin/migrate-assets?dry_run=false`, null, { headers });
      toast.info(`Migration gestartet (${result.found} Assets)...`);
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const status = await axios.get(`${API}/admin/migrate-assets/status`, { headers });
        if (!status.data.running && status.data.result && !status.data.result.dry_run) {
          const r = status.data.result;
          if (r.errors.length > 0) toast.error(`${r.migrated} migriert, ${r.errors.length} Fehler`);
          else toast.success(`${r.migrated} Assets erfolgreich migriert!`);
          return;
        }
      }
      toast.error("Migration Timeout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration fehlgeschlagen");
    } finally {
      setMigrateLoading(false);
    }
  };

  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Bitte eine .zip Backup-Datei wählen");
      return;
    }
    if (!window.confirm(
      `ACHTUNG: Die aktuellen Daten werden vollständig durch das Backup „${file.name}" ersetzt (Events, News, Galerie, Mitglieder, Bilder usw.).\n\nDieser Vorgang kann NICHT rückgängig gemacht werden. Fortfahren?`
    )) return;
    const token = localStorage.getItem("nb_token");
    setRestoreLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/restore`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const r = res.data;
      const total = Object.values(r.collections || {}).reduce((a, c) => a + c, 0);
      toast.success(`Wiederherstellung fertig: ${total} Einträge, ${r.images} Bilder. Bitte ggf. neu anmelden.`);
      setTimeout(() => loadData(), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wiederherstellung fehlgeschlagen");
    } finally {
      setRestoreLoading(false);
    }
  };

  const openDialog = (type, defaults = {}, id = null) => {
    setFormData(defaults);
    setDialog(type);
    setEditingId(id);
  };

  const closeDialog = () => { setDialog(null); setEditingId(null); };

  const syncFromLive = async () => {
    const base = "Inhalte (Events, Neuigkeiten, Galerie, Getränkekarte, Sponsoren)";
    const extra = syncMembers
      ? " sowie Mitglieder & Admin-Konten"
      : "";
    if (!window.confirm(`${base}${extra} von der Live-Seite ninersbillard.com laden? Die aktuellen Vorschau-Daten werden dabei ersetzt.${syncMembers ? " Hinweis: Mitglieder-Passwörter werden mit übernommen." : ""}`)) return;
    const token = localStorage.getItem("nb_token");
    const headers = { Authorization: `Bearer ${token}` };
    setSyncLoading(true);
    try {
      await axios.post(`${API}/admin/sync-from-live`, null, { headers, params: { include_members: syncMembers } });
      toast.info("Sync von Live gestartet...");
      for (let i = 0; i < 90; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const s = await axios.get(`${API}/admin/sync-from-live/status`, { headers });
        if (!s.data.running && s.data.result) {
          const r = s.data.result;
          const total = Object.values(r.collections).reduce((a, c) => a + (c.count || 0), 0);
          let msg = `Sync fertig: ${total} Einträge geladen, ${r.images.synced} neue Bilder`;
          if (r.protected) {
            if (r.protected.error) {
              msg += ` · Mitglieder-Sync übersprungen: ${r.protected.error}`;
            } else {
              const m = r.protected.members?.count ?? 0;
              const u = r.protected.users?.count ?? 0;
              msg += ` · ${m} Mitglieder, ${u} Admins`;
            }
          }
          toast.success(msg);
          loadData();
          return;
        }
      }
      toast.error("Sync Timeout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync fehlgeschlagen");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSave = async () => {
    const isEdit = !!editingId;
    // One auto-retry for idempotent edits on transient (timeout/5xx) failures —
    // mirrors the manual "second attempt works" behaviour seen in production.
    const run = async (fn) => {
      try {
        return await fn();
      } catch (e) {
        const transient = !e.response || e.response.status >= 500;
        if (isEdit && transient) {
          await new Promise(r => setTimeout(r, 700));
          return await fn();
        }
        throw e;
      }
    };
    try {
      if (dialog === "event") {
        if (isEdit) { await run(() => api.put(`/events/${editingId}`, formData)); toast.success("Event aktualisiert"); }
        else { await api.post("/events", formData); toast.success("Event erstellt"); }
      } else if (dialog === "news") {
        if (isEdit) { await run(() => api.put(`/news/${editingId}`, formData)); toast.success("Nachricht aktualisiert"); }
        else { await api.post("/news", formData); toast.success("Nachricht erstellt"); }
      } else if (dialog === "gallery") {
        const galleryPayload = { title: formData.title, description: formData.description, club_id: formData.club_id, image_url: formData.image_url || "", image_urls: formData.image_urls || [] };
        if (isEdit) { await run(() => api.put(`/gallery/${editingId}`, galleryPayload)); toast.success("Galerieeintrag aktualisiert"); }
        else { await api.post("/gallery", galleryPayload); toast.success("Galerieeintrag erstellt"); }
      } else if (dialog === "pricelist") {
        if (isEdit) { await run(() => api.put(`/pricelist/${editingId}`, formData)); toast.success("Eintrag aktualisiert"); }
        else { await api.post("/pricelist", formData); toast.success("Eintrag erstellt"); }
      } else if (dialog === "offer") {
        if (isEdit) { await run(() => api.put(`/special-offers/${editingId}`, formData)); toast.success("Angebot aktualisiert"); }
        else { await api.post("/special-offers", formData); toast.success("Angebot erstellt"); }
      }
      closeDialog();
      loadData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      const text = typeof detail === "string" ? detail
        : (e.code === "ECONNABORTED" || !e.response) ? "Zeitüberschreitung – bitte erneut versuchen"
        : `Fehler beim Speichern${status ? ` (${status})` : ""}`;
      toast.error(text);
    }
  };

  const handleDelete = async (type, id) => {
    try {
      await api.del(`/${type}/${id}`);
      toast.success("Gelöscht");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvUploading(true);
    const data = new FormData();
    data.append("file", file);
    try {
      const res = await api.upload("/pricelist/import-csv", data);
      toast.success(`${res.data.imported} Artikel importiert`);
      loadData();
    } catch (err) {
      toast.error("CSV Import fehlgeschlagen");
    } finally {
      setCsvUploading(false);
      e.target.value = "";
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="sticky top-0 z-50 bg-background/80 glass-header border-b border-white/10">
        <div className="flex items-center justify-between px-4 md:px-8 h-14">
          <div className="flex items-center gap-3">
            <span className="font-bold text-lg uppercase tracking-tight" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>
              Admin
            </span>
            <Badge variant="outline" className="text-xs">{user.display_name}</Badge>
            <Badge className="text-xs">{isCentral ? "Zentraladmin" : user.club_id}</Badge>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/qr-codes" data-testid="admin-qr-link"
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground hover:text-primary transition-colors duration-200">
              <QrCode className="w-4 h-4" /> QR Codes
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              <LogOut className="w-4 h-4" /> Abmelden
            </button>
            {isCentral && (
              <button onClick={downloadBackup} disabled={backupLoading} data-testid="admin-backup"
                className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 disabled:opacity-50">
                <HardDrive className={`w-4 h-4 ${backupLoading ? 'animate-pulse' : ''}`} />
                {backupLoading ? "Backup wird erstellt..." : "Backup"}
              </button>
            )}
            {isCentral && (
              <>
                <input ref={restoreInputRef} type="file" accept=".zip,application/zip"
                  onChange={handleRestoreFile} className="hidden" data-testid="admin-restore-input" />
                <button onClick={() => restoreInputRef.current?.click()} disabled={restoreLoading}
                  data-testid="admin-restore"
                  className="flex items-center gap-1 text-sm text-emerald-400 hover:text-emerald-300 disabled:opacity-50">
                  <Upload className={`w-4 h-4 ${restoreLoading ? 'animate-pulse' : ''}`} />
                  {restoreLoading ? "Wird wiederhergestellt..." : "Wiederherstellen"}
                </button>
              </>
            )}
            {isCentral && (
              <button onClick={migrateAssets} disabled={migrateLoading} data-testid="admin-migrate-assets"
                className="flex items-center gap-1 text-sm text-orange-400 hover:text-orange-300 disabled:opacity-50">
                <ArrowRightLeft className={`w-4 h-4 ${migrateLoading ? 'animate-spin' : ''}`} />
                {migrateLoading ? "Migration läuft..." : "Assets migrieren"}
              </button>
            )}
            {isCentral && (
              <button onClick={syncFromLive} disabled={syncLoading} data-testid="admin-sync-live"
                className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 disabled:opacity-50">
                <DownloadCloud className={`w-4 h-4 ${syncLoading ? 'animate-bounce' : ''}`} />
                {syncLoading ? "Sync läuft..." : "Von Live laden"}
              </button>
            )}
            {isCentral && (
              <label className="flex items-center gap-1.5 text-sm text-zinc-400 cursor-pointer select-none" data-testid="admin-sync-members-label">
                <input type="checkbox" checked={syncMembers} disabled={syncLoading}
                  onChange={(e) => setSyncMembers(e.target.checked)}
                  data-testid="admin-sync-members-checkbox"
                  className="accent-blue-500 w-3.5 h-3.5" />
                inkl. Mitglieder/Admins
              </label>
            )}
          </div>
        </div>
      </header>

      <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
        <Tabs defaultValue="events" data-testid="admin-tabs">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-8">
            {[
              { v: "events", l: "Termine", i: CalendarDays },
              { v: "news", l: "Neuigkeiten", i: Newspaper },
              { v: "gallery", l: "Galerie", i: Image },
              ...(isCentral ? [
                { v: "pricelist", l: "Getränkekarte", i: DollarSign },
                { v: "offers", l: "Angebote", i: Tag },
              ] : []),
              { v: "reservations", l: "Reservierungen", i: BookOpen },
              { v: "members", l: "Mitglieder", i: Users },
              { v: "clubs", l: "Vereine", i: BookOpen },
              ...(isCentral ? [{ v: "admins", l: "Admins", i: Shield }] : []),
              ...(isCentral ? [{ v: "sponsors", l: "Sponsoren", i: Crown }] : []),
              ...(isCentral ? [{ v: "downloads", l: "Downloads", i: FolderDown }] : []),
              ...(isCentral ? [{ v: "scrape", l: "Liga-Daten", i: RefreshCw }] : []),
              ...(isCentral ? [{ v: "logs", l: "Logs", i: ScrollText }] : []),
              ...(isCentral ? [{ v: "email", l: "E-Mail", i: Mail }] : []),
            ].map(tab => (
              <TabsTrigger key={tab.v} value={tab.v} data-testid={`admin-tab-${tab.v}`}
                className="flex items-center gap-2 px-3 py-2 border border-white/10 text-xs font-bold uppercase tracking-wider data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">
                <tab.i className="w-4 h-4" /> {tab.l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Content list tabs (events, news, gallery, pricelist, offers, reservations) */}
          <AdminContentTabs
            events={events} news={news} gallery={gallery} pricelist={pricelist}
            offers={offers} reservations={reservations} isCentral={isCentral}
            managedClubs={managedClubs} openDialog={openDialog} handleDelete={handleDelete}
            handleCsvImport={handleCsvImport} csvUploading={csvUploading} />

          {/* Members Tab */}
          <TabsContent value="members">
            <MembersTab api={api} isCentral={isCentral} user={user} />
          </TabsContent>

          {/* Clubs Tab */}
          <TabsContent value="clubs">
            <ClubsTab api={api} isCentral={isCentral} user={user} managedClubs={managedClubs} />
          </TabsContent>

          {/* Admins Tab (central only) */}
          {isCentral && (
            <TabsContent value="admins">
              <AdminsTab api={api} />
            </TabsContent>
          )}

          {/* Sponsors Tab (central only) */}
          {isCentral && (
            <TabsContent value="sponsors">
              <SponsorsTab api={api} />
            </TabsContent>
          )}

          {/* Downloads Tab (central only) */}
          {isCentral && (
            <TabsContent value="downloads">
              <DownloadsTab api={api} />
            </TabsContent>
          )}

          {/* Scrape Tab (central only) */}
          {isCentral && (
            <TabsContent value="scrape">
              <ScrapeTab api={api} />
            </TabsContent>
          )}

          {/* Logs Tab (central only) */}
          {isCentral && (
            <TabsContent value="logs">
              <LogsTab api={api} />
            </TabsContent>
          )}

          {/* Email Tab (central only) */}
          {isCentral && (
            <TabsContent value="email">
              <EmailTab api={api} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Create / Edit Dialog */}
      <ContentDialog
        dialog={dialog} formData={formData} setFormData={setFormData} editingId={editingId}
        managedClubs={managedClubs} api={api} onSave={handleSave} onClose={closeDialog} />
    </div>
  );
}
